import type { ScopeQuery } from "@app/shared"
import { z } from "zod"
import { http } from "../lib/http"

/**
 * Party role enum. Mirrors `party_role` pgEnum from `@app/db`.
 * `notify1..5` model the >=4-deep notify chains seen in real BL
 * artefacts (Wincom -> CVAP -> Multitrade Africa, etc.).
 */
export const PartyRoleSchema = z.enum([
	"shipper",
	"consignee",
	"notify1",
	"notify2",
	"notify3",
	"notify4",
	"notify5",
	"forwarder",
	"agent",
	"customs_broker",
])

export type PartyRole = z.infer<typeof PartyRoleSchema>

/**
 * Mirrors `ShipmentPartyDto` -- party joined with its per-shipment
 * role (snapshot at link time).
 */
export const ShipmentPartySchema = z.object({
	partyId: z.string(),
	roleOnShipment: PartyRoleSchema,
	name: z.string(),
	intrinsicRole: PartyRoleSchema,
	parentPartyId: z.string().nullable(),
	countryCode: z.string().nullable(),
	unlocode: z.string().nullable(),
	linkedAt: z.string(),
})

export const PartyDetailSchema = z.object({
	id: z.string(),
	name: z.string(),
	role: PartyRoleSchema,
	parentPartyId: z.string().nullable(),
	countryCode: z.string().nullable(),
	unlocode: z.string().nullable(),
	address: z.string().nullable(),
	contactEmail: z.string().nullable(),
	contactPhone: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
})

/**
 * Mirrors `PartySummaryDto` -- lean projection for `GET /parties`
 * tenant-wide list. Omits contact / address / audit fields.
 */
export const PartySummarySchema = z.object({
	id: z.string(),
	name: z.string(),
	role: PartyRoleSchema,
	parentPartyId: z.string().nullable(),
	countryCode: z.string().nullable(),
	unlocode: z.string().nullable(),
})

const PartyListSchema = z.array(ShipmentPartySchema)
const PartyTenantListSchema = z.array(PartySummarySchema)

export type ShipmentParty = z.infer<typeof ShipmentPartySchema>
export type PartySummary = z.infer<typeof PartySummarySchema>
export type PartyDetail = z.infer<typeof PartyDetailSchema>

/** GET /shipments/:shipmentId/parties */
export const listShipmentParties = async (
	shipmentId: string,
	signal?: AbortSignal,
): Promise<ShipmentParty[]> => {
	const raw = await http<unknown>(
		`/shipments/${encodeURIComponent(shipmentId)}/parties`,
		signal !== undefined ? { signal } : {},
	)
	return PartyListSchema.parse(raw)
}

/**
 * GET /parties -- tenant-wide list (alphabetical by name, top 100).
 *
 * Optional `q` is a case-insensitive substring filter against `name`
 * + `id` (matches the engine's `PartiesService.listForTenant`).
 * Empty/whitespace `q` = no filter (same as omitting it).
 *
 * Scope is COUNTRY ONLY (decision 5): the engine ignores vertical for
 * parties, so we send only `country` and never `vertical` — a
 * vertical-only scope change must not refetch (identical results).
 */
export const listParties = async (
	options: { q?: string; scope?: ScopeQuery; signal?: AbortSignal } = {},
): Promise<PartySummary[]> => {
	const trimmed = options.q?.trim() ?? ""
	const search = new URLSearchParams()
	if (trimmed.length > 0) search.set("q", trimmed)
	if (options.scope?.country) search.set("country", options.scope.country)
	const qs = search.toString()
	const path = qs ? `/parties?${qs}` : "/parties"
	const raw = await http<unknown>(
		path,
		options.signal !== undefined ? { signal: options.signal } : {},
	)
	return PartyTenantListSchema.parse(raw)
}

/** GET /parties/:id */
export const getParty = async (id: string, signal?: AbortSignal): Promise<PartyDetail> => {
	const raw = await http<unknown>(
		`/parties/${encodeURIComponent(id)}`,
		signal !== undefined ? { signal } : {},
	)
	return PartyDetailSchema.parse(raw)
}

/**
 * GET /parties/export -- CSV text for the current search + country scope.
 * Vertical is accepted but changes nothing: parties have no vertical link.
 */
export const exportPartiesCsv = async (
	options: { q?: string; scope?: ScopeQuery; signal?: AbortSignal } = {},
): Promise<string> => {
	const trimmed = options.q?.trim() ?? ""
	const search = new URLSearchParams()
	if (trimmed.length > 0) search.set("q", trimmed)
	if (options.scope?.country) search.set("country", options.scope.country)
	const qs = search.toString()
	return http<string>(qs ? `/parties/export?${qs}` : "/parties/export", {
		headers: { Accept: "text/csv" },
		...(options.signal !== undefined ? { signal: options.signal } : {}),
	})
}

/**
 * Body for `PATCH /parties/:id` — send only what changed.
 *
 * No `role` or `parentPartyId`: a party's role is what it IS on every shipment
 * it appears on, and the parent link encodes an "on behalf of" chain. Changing
 * either rewrites the meaning of existing shipments rather than fixing a typo.
 */
export interface UpdatePartyInput {
	name?: string
	countryCode?: string | null
	unlocode?: string | null
	address?: string | null
	contactEmail?: string | null
	contactPhone?: string | null
}

/** PATCH /parties/:id */
export const updateParty = async (id: string, input: UpdatePartyInput): Promise<PartyDetail> => {
	const raw = await http<unknown>(`/parties/${encodeURIComponent(id)}`, {
		method: "PATCH",
		json: input,
	})
	return PartyDetailSchema.parse(raw)
}

export const partiesQueryKeys = {
	all: ["parties"] as const,
	// COUNTRY ONLY (decision 5): vertical never enters the key, so a
	// vertical-only scope change does not refetch parties. Scope segment is
	// appended only when set, keeping `list()` a valid invalidation prefix.
	list: (q?: string, scope?: ScopeQuery): readonly string[] => {
		const trimmed = q?.trim() ?? ""
		const key: string[] = [...partiesQueryKeys.all, "list", "tenant"]
		if (trimmed.length > 0) key.push("q", trimmed)
		if (scope?.country) key.push(scope.country)
		return key
	},
	listByShipment: (shipmentId: string) =>
		[...partiesQueryKeys.all, "list", "shipment", shipmentId] as const,
	detail: (id: string) => [...partiesQueryKeys.all, "detail", id] as const,
}
